// LoadingLine — the shuttle-bar loading indicator that replaces the
// plain "Loading…" text throughout the app.
//
// The label reflects the query actually in flight (not the query the
// page cares about) — read from React Query's cache in real time. If
// several are running, we prioritize the caller's expected query when
// provided; otherwise we pick whichever fetch started first.
//
// Two usage shapes:
//   <LoadingLine />               — auto-labels from the active query
//   <LoadingLine label="…" />     — override with a fixed label
import { useCallback, useSyncExternalStore } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";

/** Human-readable labels for query keys. Keyed by joined string of the
 *  key parts so ["squapi", "flags"] → "squapi/flags". Prefix match is
 *  used for parameterized keys like ["feature-entries", "abc-123"]. */
const QUERY_LABELS: Record<string, string> = {
  "me": "Verifying you",
  "feature-entries": "Loading feature launches",
  "squapi/flags": "Loading feature flags",
  "squapi/workspaces": "Loading workspaces",
  "squapi/flags/unused": "Loading unused flags",
  "feed": "Loading timeline",
  "feature-families": "Loading families",
  "releases": "Loading releases",
  "release-items": "Loading release items",
  "prompts": "Loading prompts",
  "staging": "Loading staging",
  "unified-releases": "Loading releases",
};

function keyToPath(key: QueryKey): string {
  return key.map((p) => String(p)).join("/");
}

function labelFor(key: QueryKey): string {
  const path = keyToPath(key);
  if (QUERY_LABELS[path]) return QUERY_LABELS[path];
  // Longest-prefix wins so a parameterized key like "feature-entries/abc"
  // matches "feature-entries" before falling back to the generic label.
  const prefixes = Object.keys(QUERY_LABELS).sort((a, b) => b.length - a.length);
  for (const prefix of prefixes) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return QUERY_LABELS[prefix]!;
    }
  }
  return "Loading";
}

export function LoadingLine({ label }: { label?: string }) {
  const auto = useActiveQueryLabel();
  const shown = label ?? auto ?? "Loading";
  return (
    <div className="flex flex-col items-center gap-2.5 py-10">
      <div className="relative h-1 w-56 overflow-hidden rounded-full bg-border">
        <div className="absolute inset-y-0 left-0 w-1/3 animate-loading-shuttle rounded-full bg-gradient-to-r from-transparent via-accent to-transparent" />
      </div>
      <p className="text-xs text-text-secondary">{shown}…</p>
    </div>
  );
}

/** Subscribe to the query cache and return the label of whichever query
 *  is currently fetching. Returns null when nothing is in flight so
 *  callers can fall back to their own label.
 *
 *  Uses useSyncExternalStore so cache updates that fire during another
 *  component's render (e.g. a peer starting a fetch) don't set state
 *  during that render — React 18 warns loudly about that pattern. The
 *  snapshot returns a plain string/null so Object.is comparisons between
 *  successive snapshots are cheap and correct. */
function useActiveQueryLabel(): string | null {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient],
  );
  const getSnapshot = useCallback(() => {
    const running = queryClient.getQueryCache().findAll({ fetchStatus: "fetching" });
    const first = running[0];
    return first ? labelFor(first.queryKey) : null;
  }, [queryClient]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
